import React, { useEffect } from 'react';
import { useParams, Redirect, Link } from 'react-router-dom';
import { useDispatch, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ForgetDeviceButton, CopyButton, PageTitle, Loader, PrinterResume, EftResume, CollectorResume } from '../../../../components';
import { loadHost, updateHost, loadHosts, loadCollectors, loadPrinters, loadEfts } from '../../../../redux';
import { Row, Col, Badge, ButtonGroup, Button } from 'reactstrap';
import { Card, CardHeader, CardImg, CardBody, CardTitle, CardSubtitle } from 'reactstrap';
import hostImage from '../../../../assets/images/host.png';
import Icon from '@mdi/react';
import { useSelector } from 'react-redux';
import {
    mdiSlotMachine, mdiArrowLeftCircle, mdiWall,
    mdiAndroidMessages, mdiBankTransfer
} from '@mdi/js';
import HostForm from './HostForm/HostForm';
import HostActions from './HostActions/HostActions';

import './HostManagement.css';

const HostManagement = () => {
    const { t }         = useTranslation();
    const dispatch      = useDispatch();
    const { hostId } = useParams();

    const { connected } = useSelector(state => state.sioBridge);
    const {
        host, isLoadingOne,
        loadingOneErrorMessage, successfullyForgot,
    } = useSelector(state => state.hosts);

    const { collectors, isLoading: isLoadingCollectors } = useSelector(state => state.collectors);
    const { printers, isLoading: isLoadingPrinters }     = useSelector(state => state.printers);
    const { efts, isLoading: isLoadingEfts }             = useSelector(state => state.efts);
    const isLoadingConnectedDevices = isLoadingCollectors || isLoadingPrinters || isLoadingEfts;

    useEffect(() => {
        if (connected)
            dispatch(loadHost(hostId));
    }, [dispatch, connected, hostId]);

    useEffect(() => {
        if (!host)
            return;

        const devices = [].concat(printers, efts, collectors)
            ?.filter(p => p?.host_id === host.id)
        devices.forEach(p => host.connectDevice(p));
    }, [host, printers, efts, collectors]);

    const refresh = () => {
        if (connected) {
            dispatch(loadCollectors());
            dispatch(loadPrinters());
            dispatch(loadEfts());
        }
    };

    const saveOptions = host => {
        if (('only_for_pos' in host.options) && !host.options.only_for_pos.length)
            delete host.options.only_for_pos;
        dispatch(updateHost(host));
    }

    if (successfullyForgot)
        return <Redirect to="/devices" />

    const actions = (
        <ButtonGroup>
            <ForgetDeviceButton device={ host } />
            <Button tag={Link} to="/devices" color="info">
                <Icon path={ mdiArrowLeftCircle } size={ 1 } color="white" /> { t('common.back') }
            </Button>
        </ButtonGroup>
    );

    return (
        <div className="HostManagement">
            <Row>
                <Col>
                    <PageTitle
                        title={ t('hosts.manage_title', { name: host?.name }) }
                        icon="cube"
                        actions={ actions }
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    { loadingOneErrorMessage && (
                        <div className="alert alert-danger">
                            { loadingOneErrorMessage }
                        </div>
                    )}
                    {host && (
                    <div>
                        <Row>
                            <Col sm={host?.options ? 4 : 6}>
                                <Card className="card-outline">
                                    <CardHeader>
                                        <CardTitle tag="h4">
                                            <i className="fa fa-info-circle" />{' '}
                                            { t('hosts.infos') }
                                        </CardTitle>
                                    </CardHeader>
                                    <CardBody>
                                        <div className="info">
                                            <label>{ t('hosts.id') }</label>
                                            <span className="text-italic">{ host?.id }</span>
                                        </div>
                                        <div className="info">
                                            <label>{ t('hosts.name') }</label>
                                            <span>{ host?.name }</span>
                                        </div>
                                        {host?.options?.model && (
                                            <div className="info">
                                                <label>{ t('hosts.model') }</label>
                                                <span>{ host.options.model }</span>
                                            </div>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col sm={host?.options ? 4 : 6}>
                                <Card className="card-outline">
                                    <CardHeader>
                                        <CardTitle tag="h4">
                                            <i className="fa fa-object-group" />{' '}
                                            { t('hosts.pihyve_service') }
                                        </CardTitle>
                                    </CardHeader>
                                    <CardBody>
                                        {host?.options?.pihyve && (
                                            <div className="info">
                                                <label>{ t('hosts.pihyve_version') }</label>
                                                <span>
                                                    <Badge color="dark">
                                                        { host.options.pihyve.version }
                                                    </Badge>
                                                </span>
                                            </div>
                                        )}
                                        <div className="info">
                                            <label>{ t('hosts.last_connexion') }</label>
                                            <span>{ host?.getLastConnectedAt() }</span>
                                        </div>
                                        <div className="info">
                                            <label>{ t('hosts.status') }</label>
                                            <span>
                                                <Badge color={ host.isConnected() ? 'success' : 'danger' }>
                                                    { t(`hosts.${host.isConnected() ? 'connected' : 'disconnected'}`) }<br/>
                                                </Badge>
                                            </span>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                            {host?.options && (
                                <Col sm={4}>
                                    <Card className="card-outline">
                                        <CardHeader>
                                            <CardTitle tag="h4">
                                                <i className="fa fa-wifi" />{' '}
                                                { t('hosts.network') }
                                            </CardTitle>
                                        </CardHeader>
                                        <CardBody>
                                            <div className="info">
                                                <label>{ t('hosts.hostname') }</label>
                                                <CopyButton textToCopy={ host?.options?.hostname } />
                                            </div>
                                            {Object.keys(host.options.networkInterfaces || {}).filter(i => i !== 'lo').map(i => (
                                                <div key={i} className="info">
                                                    <label>{ i }</label>
                                                    <CopyButton  textToCopy={ host.options.networkInterfaces[i].filter(c => c.family === 'IPv4').map(c => c.address).join(', ') } />
                                                </div>
                                            ))}
                                        </CardBody>
                                    </Card>
                                </Col>
                            )}
                        </Row>
                        <Row>
                            <Col>
                                <Card className="card-outline">
                                    <CardBody>
                                        <div className="info">
                                            <label>{ t('hosts.connected_devices') }</label>
                                            <Button color="primary" size="xs" className="mr-3" onClick={refresh}>
                                                <i className={`fa fa-${isLoadingConnectedDevices ? 'spinner fa-spin' : 'refresh'}`} />&nbsp;
                                            </Button>
                                            {host?.getConnectedDevices()?.map((device, index) => (
                                                <Button key={index} tag={ Link } to={`/devices/${device.type}s/manage/${device.id}`} color={device.isConnected() ? 'success' : 'danger'} size="xs" className="mr-2">
                                                    <i className={`fa fa-${device.getIconName()}`} />&nbsp;
                                                    {device.name}
                                                </Button>
                                            ))}
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Card className="card-outline">
                                    <CardHeader>
                                        <CardTitle tag="h4">
                                            <i className="fa fa-gear" />{' '}
                                            { t('hosts.options') }
                                        </CardTitle>
                                    </CardHeader>
                                    <CardBody>
                                        <HostForm initialValues={ host } onSubmit={ saveOptions } isLoading={ isLoadingOne }/>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Card className="card-outline">
                                    <CardHeader>
                                        <CardTitle tag="h4" className="d-flex justify-between">
                                            <div>
                                                <i className="fa fa-terminal" />{' '}
                                                { t('hosts.actions') }
                                            </div>
                                            {isLoadingOne && (
                                                <Loader size={1} className="m-0"/>
                                            )}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardBody>
                                        <HostActions hostId={hostId} />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                    )}
                </Col>
            </Row>
        </div>
    );
}

export default HostManagement;
